import { Space, message, Skeleton, Popover, Table, Tooltip, Spin } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import { useState, useEffect, useRef, useCallback } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { PlusOutlined } from '@ant-design/icons';

import { SUB_USERS } from 'events/types';
import AddNewSubUserForm from 'pages/Dialogs/AddNewSubUserForm';
import { getSubUsers, resendLink } from 'services/sub-users';
import { toastError } from 'services/helpers/getError';
import { setCulture } from 'services/others';

import useTranslation from '../../customHooks/translate';

const SubUsers = () => {
  const translation = useTranslation();
  const [addSubUserFormOpen, setAddSubUserFormOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('language')
  );
  const [isLanguageChanging, setIsLanguageChanging] = useState(false);

  const [pageSize] = useState(15);
  const [pageIndex, setPageIndex] = useState(0);
  const [tableData, setTableData] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const isComponentMounted = useRef(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const resetData = () => {
    setTableData([]);
    setPageIndex(0);
    setHasMore(true);
  };

  const fetchData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      if (isNewRequest) {
        setPageIndex(0);
      }

      try {
        const response = await getSubUsers(pageSize, currentPage);

        const data = response?.data?.data ?? [];

        if (data.length) {
          const newData = data.map((item: any) => ({
            ...item,
            key: item.id,
          }));

          if (isNewRequest) {
            setTableData(newData);
          } else {
            setTableData((prev) => [...prev, ...newData]);
          }

          setHasMore(newData.length >= pageSize);

          return response.data;
        }

        if (isNewRequest) {
          setTableData([]);
        }
        setHasMore(false);
        return null;
      } catch (error: any) {
        message.error(error?.response?.data?.Message);
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [pageSize]
  );

  const { isLoading, refetch } = useQuery(
    [SUB_USERS, currentLanguage],
    () => fetchData(0, true),
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      enabled: !isLanguageChanging,
      staleTime: 0,
      cacheTime: 60000,
    }
  );

  useEffect(() => {
    const checkLanguageAndUpdateBackend = async () => {
      const storedLanguage = localStorage.getItem('language');
      if (storedLanguage && storedLanguage !== currentLanguage) {
        setIsLanguageChanging(true);

        try {
          await setCulture(storedLanguage);

          setCurrentLanguage(storedLanguage);

          await new Promise((resolve) => setTimeout(resolve, 300));

          await refetch();
        } catch {
        } finally {
          setIsLanguageChanging(false);
        }
      }
    };

    checkLanguageAndUpdateBackend();

    const handleStorageChange = () => {
      checkLanguageAndUpdateBackend();
    };

    window.addEventListener('storage', handleStorageChange);

    const intervalId = setInterval(checkLanguageAndUpdateBackend, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [currentLanguage, refetch]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isComponentMounted.current) {
        resetData();
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);

  useEffect(() => {
    isComponentMounted.current = true;
    resetData();
    refetch();
    return () => {
      isComponentMounted.current = false;
    };
  }, [refetch]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (isComponentMounted.current) {
        if (tableData.length === 0) {
          refetch();
        }
      }
    };

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [refetch, tableData.length]);

  const handleScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMore ||
      isLoading
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchData(nextPage, false);
    }
  }, [pageIndex, hasMore, isLoading, fetchData]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  const handleResendLink = async (email: string) => {
    try {
      const payload = {
        email,
      };
      await resendLink(payload);
      message.success(translation.linkResent);
    } catch (error) {
      toastError(error);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: <span>{translation.subusers.Name}</span>,
      dataIndex: 'fullName',
      key: 'title',
      align: 'center',
      width: 300,
    },
    {
      title: <span>{translation.subusers.Username}</span>,
      dataIndex: 'username',
      key: 'username',
      align: 'center',
      width: 300,
    },
    {
      title: <span>{translation.subusers.ExpiryDate}</span>,
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      align: 'center',
      width: 300,

      render: (expiryDate) => expiryDate.slice(0, 10),
    },
    {
      title: <span>{translation.subusers.Status}</span>,
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 300,
    },

    {
      title: <span>{translation.action.Action}</span>,
      dataIndex: 'actions',
      align: 'center',
      key: 'actions',
      width: 200,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Link to={`/profile/permission-sub-user/${record.id}`}>
                  <div style={{ cursor: 'pointer' }}>
                    <img
                      src="assets/edit-icon.svg"
                      alt=""
                      onClick={() => {
                        setAddSubUserFormOpen(true);
                        localStorage.setItem('subUserName', record?.username);
                      }}
                    />
                  </div>
                </Link>

                {record.isInitial === true && (
                  <Tooltip title={translation.subusers.ResendLink}>
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleResendLink(record.email)}
                    >
                      <LinkOutlined
                        style={{
                          fontSize: '16px',
                          color: '#50525a',
                          padding: '4px',
                          border: '1px solid #ccc',
                          borderRadius: '6px',
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            {record.isInitial === true && (
              <Tooltip title={translation.subusers.ResendLink}>
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleResendLink(record.email)}
                >
                  <LinkOutlined
                    style={{
                      fontSize: '16px',
                      color: '#50525a',
                      padding: '4px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                    }}
                  />
                </div>
              </Tooltip>
            )}

            <Link to={`/profile/permission-sub-user/${record.id}`}>
              <div style={{ cursor: 'pointer' }}>
                <img
                  src="assets/edit-icon.svg"
                  alt=""
                  onClick={() => {
                    setAddSubUserFormOpen(true);
                    localStorage.setItem('subUserName', record?.username);
                  }}
                />
              </div>
            </Link>
          </Space>
        ),
    },
  ];

  return (
    <>
      <div className="mb-[20px] flex w-full flex-wrap items-center justify-between gap-[10px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.subusers.SubUsers}
        </p>
        <button
          onClick={() => setAddSubUserFormOpen(true)}
          className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
        >
          <PlusOutlined />
          <p className="hidden md:block">
            {translation.subusers.AddNewSubUser}
          </p>
        </button>
      </div>
      <AddNewSubUserForm
        open={addSubUserFormOpen}
        handleClose={() => {
          setAddSubUserFormOpen(false);
        }}
      />
      {isLoading && tableData.length === 0 ? (
        <Skeleton active />
      ) : (
        <div
          className="relative h-[500px] overflow-y-auto"
          ref={tableContainerRef}
        >
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            className="custom-bordered"
            bordered
            scroll={{ x: 'max-content' }}
            dataSource={tableData}
            columns={columns as any[]}
            loading={isLoading && pageIndex === 0}
            pagination={false}
            rowClassName={(_, i) =>
              i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
            }
          />
          <LoadingIndicator ref={loadingRef}>
            {isLoadingMore && <Spin size="small" />}
            {!hasMore && tableData.length > 0 && (
              <div style={{ color: '#999', marginTop: '8px' }}>
                {translation.messages.allData}
              </div>
            )}
          </LoadingIndicator>
        </div>
      )}
    </>
  );
};

export default SubUsers;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
`;
